/**
 * Data for the dashboard's lists and its "Needs attention" strip.
 *
 * Every list here is a thin view over an endpoint a full page already uses (the users list, the
 * admin adventures list, the ticket queues), sorted newest first. The dashboard deliberately does
 * not get its own summary endpoint: a second copy of "what counts as a recent user" is how the
 * dashboard and the Users page start disagreeing.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { adminApi } from '@api/endpoints/admin'
import {
  getTicketPool,
  getTicketsByStatus,
  getTicketStatistics,
} from '@api/services/tickets.api'
import { TicketStatus } from '@api/types/tickets'

/** Short enough that a moderator coming back to the tab sees new sign-ups without a reload. */
const LIST_STALE_MS = 60_000

export function useRecentUsers(page: number, pageSize: number, search: string) {
  return useQuery({
    queryKey: ['dashboard', 'recent-users', page, pageSize, search],
    // CreatedAt descending is the server's default branch, named explicitly so it survives a
    // change of default.
    queryFn: () =>
      adminApi.getUsers({
        page: page + 1,
        pageSize,
        search: search || undefined,
        sortBy: 'CreatedAt',
        sortDescending: true,
      }),
    staleTime: LIST_STALE_MS,
    placeholderData: keepPreviousData,
  })
}

export function useRecentAdventures(page: number, pageSize: number, search: string) {
  return useQuery({
    queryKey: ['dashboard', 'recent-adventures', page, pageSize, search],
    queryFn: () =>
      adminApi.getAdventures({
        page: page + 1,
        pageSize,
        search: search || undefined,
        sortBy: 'CreatedDate',
        sortDescending: true,
      }),
    staleTime: LIST_STALE_MS,
    placeholderData: keepPreviousData,
  })
}

/**
 * Which ticket queue the dashboard shows. `pool` is the unclaimed queue a moderator picks from;
 * the rest are the server's per-status lists. "Escalated" uses the status list rather than
 * /escalated because that endpoint is Admin-only and the dashboard is also a moderator's page.
 */
export type DashboardTicketQueue = 'pool' | 'claimed' | 'inProgress' | 'awaitingUser' | 'escalated'

export const DASHBOARD_TICKET_QUEUES: { value: DashboardTicketQueue; label: string }[] = [
  { value: 'pool', label: 'Open (unclaimed)' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'inProgress', label: 'In progress' },
  { value: 'awaitingUser', label: 'Awaiting user' },
  { value: 'escalated', label: 'Escalated' },
]

const QUEUE_STATUS: Record<Exclude<DashboardTicketQueue, 'pool'>, TicketStatus> = {
  claimed: TicketStatus.Claimed,
  inProgress: TicketStatus.InProgress,
  awaitingUser: TicketStatus.AwaitingUser,
  escalated: TicketStatus.Escalated,
}

/** The ticket endpoints return a bare page with no total, so the dashboard shows one page of them. */
export const DASHBOARD_TICKET_PAGE_SIZE = 25

export function useDashboardTickets(queue: DashboardTicketQueue) {
  return useQuery({
    queryKey: ['dashboard', 'tickets', queue],
    queryFn: () =>
      queue === 'pool'
        ? getTicketPool(1, DASHBOARD_TICKET_PAGE_SIZE)
        : getTicketsByStatus(QUEUE_STATUS[queue], 1, DASHBOARD_TICKET_PAGE_SIZE),
    staleTime: LIST_STALE_MS,
  })
}

export function useDashboardTicketStats() {
  return useQuery({
    queryKey: ['dashboard', 'ticket-stats'],
    queryFn: getTicketStatistics,
    staleTime: LIST_STALE_MS,
  })
}

export function useDashboardAdventureStats() {
  return useQuery({
    queryKey: ['dashboard', 'adventure-stats'],
    queryFn: () => adminApi.getAdventureStats(),
    staleTime: LIST_STALE_MS,
  })
}
